<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">Orden de Salida</span>
        </v-card-title>

        <!-- Estadísticas -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-3">
              <div class="display-1 font-weight-bold primary--text">{{ totalOutings }}</div>
              <div class="caption grey--text">Salidas Totales</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-3">
              <div class="display-1 font-weight-bold green--text">{{ completedOutings }}</div>
              <div class="caption grey--text">Completadas</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-3">
              <div class="display-1 font-weight-bold orange--text">{{ inProgressOutings }}</div>
              <div class="caption grey--text">En Juego</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-3">
              <div class="display-1 font-weight-bold info--text">{{ pendingOutings }}</div>
              <div class="caption grey--text">Por Salir</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabla Orden de Salida -->
        <v-card>
          <v-card-subtitle class="pa-4">
            <v-icon small class="mr-2">mdi-table-large</v-icon>
            {{ todayDate }}
          </v-card-subtitle>
          <v-data-table
            :headers="headers"
            :items="todaysOutings"
            class="elevation-0"
            :no-data-text="'Sin salidas hoy'"
            sort-by="scheduled_time">

            <template v-slot:item.group_number="{ item }">
              <v-chip color="primary" text-color="white" small>
                Grupo {{ item.group_number }}
              </v-chip>
            </template>

            <template v-slot:item.players="{ item }">
              <div>
                <v-chip v-for="(player, index) in item.players" :key="index" x-small class="mr-1 mb-1">
                  {{ player }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.scheduled_time="{ item }">
              <div class="font-weight-bold">{{ item.scheduled_time }}</div>
              <div v-if="item.actual_start_time" class="caption grey--text">
                Inicio: {{ item.actual_start_time }}
              </div>
            </template>

            <template v-slot:item.current_hole="{ item }">
              <div class="text-center">
                <v-chip color="info" text-color="white" small>
                  Hoyo {{ item.current_hole }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.pace_status="{ item }">
              <v-chip :color="getPaceStatusColor(item.pace_status)" small>
                {{ getPaceStatusText(item.pace_status) }}
              </v-chip>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColor(item.status)" text-color="white" small>
                {{ getStatusLabel(item.status) }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="openMarkDialog(item)">
                    <v-icon small color="orange">mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Marcar hoyo</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" @click="openWarningDialog(item)">
                    <v-icon small color="red">mdi-alert-circle</v-icon>
                  </v-btn>
                </template>
                <span>Registrar amonestación</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Diálogo para marcar hoyo -->
    <v-dialog v-model="markDialog" max-width="500px" persistent>
      <v-card v-if="selectedOuting">
        <v-card-title>Marcar Hoyo - Grupo {{ selectedOuting.group_number }}</v-card-title>
        <v-card-text>
          <v-form ref="markForm" v-model="validMark" lazy-validation>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model.number="selectedOuting.current_hole"
                  label="Hoyo Actual"
                  type="number"
                  outlined
                  dense
                  min="1"
                  max="18"
                  :rules="[v => (v >= 1 && v <= 18) || 'Hoyo debe estar entre 1 y 18']"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="selectedOuting.pace_status"
                  :items="[
                    { text: 'En Ritmo', value: 'on_pace' },
                    { text: 'Ligeramente Atrasado', value: 'slightly_behind' },
                    { text: 'Muy Atrasado', value: 'behind_schedule' }
                  ]"
                  label="Estado de Ritmo"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="markDialog = false">Cancelar</v-btn>
          <v-btn color="primary" text @click="updateOutingHole" :disabled="!validMark">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para advertencia (amonestación) -->
    <WarningDialog
      :dialog.sync="warningDialog"
      :group="selectedOuting"
      @save="recordWarning"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import WarningDialog from './components/WarningDialog.vue'

export default {
  name: 'MarshallStarterOrder',
  components: {
    WarningDialog
  },
  data () {
    return {
      markDialog: false,
      warningDialog: false,
      validMark: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      selectedOuting: null,
      headers: [
        { text: 'Grupo', value: 'group_number', width: '10%' },
        { text: 'Jugadores', value: 'players', width: '30%', sortable: false },
        { text: 'Horario', value: 'scheduled_time', width: '12%' },
        { text: 'Hoyo', value: 'current_hole', width: '10%' },
        { text: 'Ritmo', value: 'pace_status', width: '12%' },
        { text: 'Estado', value: 'status', width: '12%' },
        { text: 'Acciones', value: 'actions', width: '14%', sortable: false, align: 'center' }
      ],
      // MOCK DATA - Salidas
      outings: [
        {
          id: 1,
          group_number: 1,
          date: '2026-08-04',
          scheduled_time: '06:30',
          actual_start_time: '06:35',
          current_hole: 12,
          players: ['Carlos López', 'Juan García', 'Roberto Díaz'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 2,
          group_number: 2,
          date: '2026-08-04',
          scheduled_time: '06:40',
          actual_start_time: '06:42',
          current_hole: 10,
          players: ['Fernando Ruiz', 'Miguel Sánchez'],
          pace_status: 'behind_schedule',
          status: 'en_juego'
        },
        {
          id: 3,
          group_number: 3,
          date: '2026-08-04',
          scheduled_time: '06:50',
          actual_start_time: '06:50',
          current_hole: 6,
          players: ['Pedro Martínez', 'Alfonso Gómez', 'Luis Cortés'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 4,
          group_number: 4,
          date: '2026-08-04',
          scheduled_time: '07:00',
          actual_start_time: '07:05',
          current_hole: 8,
          players: ['Javier Torres', 'Enrique Vega'],
          pace_status: 'slightly_behind',
          status: 'en_juego'
        },
        {
          id: 5,
          group_number: 5,
          date: '2026-08-04',
          scheduled_time: '07:10',
          actual_start_time: '07:12',
          current_hole: 3,
          players: ['Raúl Soto', 'Arturo Mendez', 'Gilberto Núñez', 'Pablo Castro'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 6,
          group_number: 6,
          date: '2026-08-04',
          scheduled_time: '07:20',
          actual_start_time: null,
          current_hole: 0,
          players: ['David Vargas', 'Santiago Reyes'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 7,
          group_number: 7,
          date: '2026-08-04',
          scheduled_time: '07:30',
          actual_start_time: null,
          current_hole: 0,
          players: ['Mauricio Jiménez', 'Héctor Fuentes', 'Gustavo Morales'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 8,
          group_number: 8,
          date: '2026-08-04',
          scheduled_time: '07:40',
          actual_start_time: '07:42',
          current_hole: 1,
          players: ['Óscar Ramírez', 'Andrés Campos'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 9,
          group_number: 9,
          date: '2026-08-04',
          scheduled_time: '07:50',
          actual_start_time: null,
          current_hole: 0,
          players: ['Vicente López', 'Ramón García', 'Jesús Martínez'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 10,
          group_number: 10,
          date: '2026-08-04',
          scheduled_time: '08:00',
          actual_start_time: null,
          current_hole: 0,
          players: ['Emilio Sánchez', 'Cristóbal Vega'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 11,
          group_number: 11,
          date: '2026-08-04',
          scheduled_time: '08:10',
          actual_start_time: null,
          current_hole: 0,
          players: ['Julio Romero', 'Ignacio Flores', 'Ricardo Ortiz'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 12,
          group_number: 12,
          date: '2026-08-04',
          scheduled_time: '18:30',
          actual_start_time: '18:32',
          current_hole: 2,
          players: ['Álvaro Castro', 'Tomás Herrera'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 13,
          group_number: 13,
          date: '2026-08-05',
          scheduled_time: '06:30',
          actual_start_time: '06:30',
          current_hole: 18,
          players: ['Xavier Mendez', 'Sergio Peña', 'Valentín Ramos'],
          pace_status: 'on_pace',
          status: 'completado'
        },
        {
          id: 14,
          group_number: 14,
          date: '2026-08-05',
          scheduled_time: '06:40',
          actual_start_time: '06:45',
          current_hole: 15,
          players: ['Leopoldo Silva', 'Benito Aguirre'],
          pace_status: 'slightly_behind',
          status: 'en_juego'
        },
        {
          id: 15,
          group_number: 15,
          date: '2026-08-05',
          scheduled_time: '06:50',
          actual_start_time: '06:52',
          current_hole: 18,
          players: ['Marcelo Gómez', 'Vicente Castillo', 'Adrián Flores'],
          pace_status: 'on_pace',
          status: 'completado'
        },
        {
          id: 16,
          group_number: 16,
          date: '2026-08-05',
          scheduled_time: '07:00',
          actual_start_time: '07:08',
          current_hole: 14,
          players: ['Andrés Silva', 'Rodrigo Montoya'],
          pace_status: 'behind_schedule',
          status: 'en_juego'
        },
        {
          id: 17,
          group_number: 17,
          date: '2026-08-05',
          scheduled_time: '07:10',
          actual_start_time: null,
          current_hole: 0,
          players: ['Mateo Contreras', 'Nicolás Reyes', 'Simón Torres'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 18,
          group_number: 18,
          date: '2026-08-05',
          scheduled_time: '07:20',
          actual_start_time: null,
          current_hole: 0,
          players: ['Hugo Pacheco', 'Gerardo Rojas'],
          pace_status: 'on_pace',
          status: 'por_salir'
        },
        {
          id: 19,
          group_number: 19,
          date: '2026-08-05',
          scheduled_time: '18:30',
          actual_start_time: '18:35',
          current_hole: 9,
          players: ['Oscar Martínez', 'Julio Navarro', 'Fernando Acosta'],
          pace_status: 'on_pace',
          status: 'en_juego'
        },
        {
          id: 20,
          group_number: 20,
          date: '2026-08-05',
          scheduled_time: '18:40',
          actual_start_time: null,
          current_hole: 0,
          players: ['Esteban Vargas', 'Lorenzo Gutiérrez'],
          pace_status: 'on_pace',
          status: 'por_salir'
        }
      ]
    }
  },
  computed: {
    todayDate () {
      const today = new Date()
      return today.toLocaleDateString('es-MX', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    todaysOutings () {
      const today = new Date().toISOString().split('T')[0]
      return this.outings.filter(o => o.date === today)
    },
    totalOutings () {
      return this.outings.length
    },
    completedOutings () {
      return this.outings.filter(o => o.status === 'completado').length
    },
    inProgressOutings () {
      return this.outings.filter(o => o.status === 'en_juego').length
    },
    pendingOutings () {
      return this.outings.filter(o => o.status === 'por_salir').length
    }
  },
  methods: {
    getStatusColor (status) {
      const colors = {
        por_salir: 'grey',
        en_juego: 'blue',
        completado: 'green'
      }
      return colors[status] || 'grey'
    },
    getStatusLabel (status) {
      const labels = {
        por_salir: 'Por Salir',
        en_juego: 'En Juego',
        completado: 'Completado'
      }
      return labels[status] || 'Desconocido'
    },
    getPaceStatusColor (status) {
      const colors = {
        on_pace: 'green',
        slightly_behind: 'orange',
        behind_schedule: 'red'
      }
      return colors[status] || 'grey'
    },
    getPaceStatusText (status) {
      const texts = {
        on_pace: 'En Ritmo',
        slightly_behind: 'Ligeramente Atrasado',
        behind_schedule: 'Muy Atrasado'
      }
      return texts[status] || 'Desconocido'
    },
    openMarkDialog (outing) {
      this.selectedOuting = { ...outing }
      this.markDialog = true
    },
    updateOutingHole () {
      if (this.$refs.markForm.validate()) {
        const index = this.outings.findIndex(o => o.id === this.selectedOuting.id)
        if (index > -1) {
          this.outings[index].current_hole = this.selectedOuting.current_hole
          this.outings[index].pace_status = this.selectedOuting.pace_status
          if (!this.outings[index].actual_start_time) {
            this.outings[index].actual_start_time = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
          }
          this.outings[index].status = this.selectedOuting.current_hole >= 18 ? 'completado' : 'en_juego'
        }
        this.snackbarMessage = `Grupo ${this.selectedOuting.group_number} marcado en hoyo ${this.selectedOuting.current_hole}`
        this.snackbarColor = 'success'
        this.snackbar = true
        this.markDialog = false
      }
    },
    openWarningDialog (outing) {
      this.selectedOuting = outing
      this.warningDialog = true
    },
    recordWarning (warning) {
      this.snackbarMessage = `Amonestación registrada: ${warning.type}`
      this.snackbarColor = 'success'
      this.snackbar = true
      this.warningDialog = false
    }
  }
}
</script>

<style scoped>
</style>
